import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useSubscribePlan, useChangeSubscriptionPlan, useContactSales } from '../hooks/use-subscription';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useSubscriptionStore } from 'state/store/subscription';
import { useIsMutating } from '@tanstack/react-query';
import { Loader2, Check, CreditCard, CircleCheck } from 'lucide-react';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import ContactModal from './contact-sales-modal';
import { billingCycleLabel, uniquePlanIntervals } from '../utils/billing-cycle';
import { isCurrentPlanOffer } from '../utils/is-current-plan-offer';
import { isIncompleteCheckout, resolvePlanSelectAction } from '../utils/resolve-plan-select-action';
import { useToast } from 'hooks/use-toast';
import { useConfig } from 'hooks/use-config';
import { useLanguageContext } from 'i18n/language-context';
import { PendingCheckoutAction } from 'features/iam/components/seats/pending-checkout-action';
import { PlanSeats } from '../utils/seat-pools';
import { useTheme } from 'components/core/theme-provider';
import { primaryButtonClass } from '../constants/button-styles';

// ── API types ──────────────────────────────────────────────────────────────────

export interface ApiPlanPrice {
  priceId: string;
  currencyCode: string;
  unitAmountMinor: number;
  interval: string;
  intervalCount: number;
  quantityItemKey: string | null;
  automaticDiscountBasisPoints?: number | null;
  displayPriceNote?: string | null;
}

export interface ApiPlanMeterTier {
  upToQuantity: number | null;
  unitAmountMinor: number;
}

export interface ApiPlanMeterRateTable {
  currencyCode: string;
  tiers: ApiPlanMeterTier[];
}

export interface ApiPlanMeter {
  meterKey: string;
  displayName: string;
  unitLabel: string;
  aggregation: string;
  includedQuantity: number;
  overageAllowed: boolean;
  thresholdPercents: number[];
  rateTables: ApiPlanMeterRateTable[];
}

export interface ApiPlanEntitlement {
  key: string;
  limitKind: 'Boolean' | 'Count' | 'Unlimited';
  limit: number | null;
  meterKey: string | null;
  unitLabel: string | null;
}

export interface ApiPlanTrialGrant {
  meterKey: string;
  includedQuantity: number;
}

export interface ApiPlanQuantityItem {
  itemKey: string;
  unitLabel: string;
  minQuantity: number;
  maxQuantity: number | null;
  defaultQuantity: number;
}

export interface ApiPlan {
  planId: string;
  code: string;
  displayName: string;
  description: string | null;
  familyCode: string;
  familyRank: number;
  usageInterval: string;
  usageIntervalCount: number;
  quantityDiscountCombinationPolicy: string;
  organizationId: string | null;
  featuresJson: string | null;
  trialDays: number | null;
  trialRequiresPaymentMethod: boolean;
  version: number;
  hasSubscribers: boolean;
  prices: ApiPlanPrice[];
  entitlements: ApiPlanEntitlement[];
  meters: ApiPlanMeter[];
  quantityItems: ApiPlanQuantityItem[];
  trialGrants: ApiPlanTrialGrant[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export const discountedAmountMinor = (
  unitAmountMinor: number,
  automaticDiscountBasisPoints?: number | null
): number => {
  if (!automaticDiscountBasisPoints) return unitAmountMinor;
  return Math.round(unitAmountMinor * (1 - automaticDiscountBasisPoints / 10000));
};

const formatCurrency = (minorAmount: number, currency = 'USD'): string => {
  const amount = minorAmount / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const isSameCataloguePlan = (
  plan: Pick<ApiPlan, 'planId' | 'code' | 'displayName'>,
  details: { subscriptionId?: string; planCode?: string; planName?: string } | null
): boolean => {
  if (!details?.subscriptionId) return false;
  return (
    details.planCode?.toLowerCase() === plan.code?.toLowerCase() ||
    details.planCode === plan.planId ||
    details.planName?.toLowerCase() === plan.displayName?.toLowerCase()
  );
};

const resolveDefaultInterval = (
  intervals: string[],
  plan: ApiPlan,
  details: { subscriptionId?: string; planCode?: string; planName?: string; priceId?: string; interval?: string } | null
): string => {
  if (intervals.length === 0) return 'month';
  if (isSameCataloguePlan(plan, details)) {
    const fromPriceId = details?.priceId
      ? plan.prices.find((p) => p.priceId === details.priceId)?.interval.toLowerCase()
      : undefined;
    const preferred = fromPriceId || details?.interval?.toLowerCase();
    if (preferred && intervals.includes(preferred)) return preferred;
  }
  if (intervals.includes('month')) return 'month';
  return intervals[0];
};

export const BillingCycleToggle = ({
  intervals,
  selectedInterval,
  disabled,
  compact,
  fullWidth,
  onChange,
}: {
  intervals: string[];
  selectedInterval: string;
  disabled?: boolean;
  compact?: boolean;
  /** Large tabs that split the whole row equally. */
  fullWidth?: boolean;
  onChange: (interval: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      aria-label={t('BILLING_CYCLE', 'Billing cycle')}
      className={cn(
        'flex w-full min-w-0 gap-0.5 rounded-full border border-[#e1dfd6] bg-[#f3f1ea] p-0.5 dark:border-border dark:bg-muted',
        intervals.length > 2 && 'flex-wrap rounded-xl',
        compact && 'w-fit max-w-full',
        fullWidth && 'gap-1 border-0 bg-[#f0ece4] p-1',
        !compact && !fullWidth && 'sm:ml-auto sm:w-auto sm:max-w-full sm:shrink-0'
      )}
    >
      {intervals.map((key) => {
        const isSelected = key === selectedInterval;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => onChange(key)}
            className={cn(
              'min-w-0 flex-1 cursor-pointer whitespace-nowrap font-semibold transition-all',
              // Same radius as the box behind the tabs.
              intervals.length > 2 ? 'rounded-xl' : 'rounded-full',
              compact && 'min-h-8 px-2.5 py-1 text-xs sm:flex-none',
              fullWidth && 'min-h-10 px-4 py-2 text-sm',
              !compact && !fullWidth && 'min-h-11 px-3 py-1.5 text-xs sm:min-h-9 sm:flex-none sm:px-3.5',
              isSelected
                ? 'bg-white text-[#171b23] shadow-sm dark:bg-background dark:text-foreground'
                : 'text-[#565f6b] hover:text-[#171b23] dark:text-muted-foreground dark:hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {billingCycleLabel(key, (k, fallback) => t(k, fallback))}
          </button>
        );
      })}
    </div>
  );
};

const billingCycleSuffix = (interval?: string): string | undefined => {
  if (!interval) return undefined;
  const normalized = interval.toLowerCase();
  if (normalized === 'day' || normalized === 'week' || normalized === 'month' || normalized === 'year') {
    return normalized;
  }
  return interval;
};

export const PLAN_INCLUDED_METER_LIMIT = 3;

export const includedPlanMeters = <T,>(meters: T[]): T[] =>
  meters.slice(0, PLAN_INCLUDED_METER_LIMIT);

const BYTES_IN_GB = 1024 ** 3;

export const bytesToGbLabel = (bytes: number): string => {
  const gb = bytes / BYTES_IN_GB;
  if (!Number.isFinite(gb) || gb < 0) return '0';
  return Number.isInteger(gb)
    ? String(gb)
    : gb.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

type MeterCopy = Pick<ApiPlanMeter, 'meterKey' | 'displayName' | 'unitLabel' | 'includedQuantity'>;
type Translate = (key: string, options?: Record<string, unknown> | string) => string;

const meterHaystack = (meter: MeterCopy): string =>
  `${meter.meterKey} ${meter.displayName} ${meter.unitLabel}`.toLowerCase();

const PLAN_BEST_FOR: Record<string, { key: string; defaultValue: string }> = {
  starter: {
    key: 'PLAN_BEST_FOR_STARTER',
    defaultValue: 'Individuals and smaller teams getting started with Business AI.',
  },
  business: {
    key: 'PLAN_BEST_FOR_BUSINESS',
    defaultValue: 'Teams running multiple AI agents, integrations and knowledge-driven workflows.',
  },
  enterprise: {
    key: 'PLAN_BEST_FOR_ENTERPRISE',
    defaultValue:
      'Larger organizations with advanced security, integration, governance or deployment requirements.',
  },
};

export const isEnterprisePlan = (code?: string) =>
  !!code && code.toLowerCase().startsWith('enterprise');

export const ENTERPRISE_INCLUDED = [
  { key: 'PLAN_ENT_CUSTOM_CREDITS', defaultValue: 'Custom KLAX Credit allocation' },
  { key: 'PLAN_ENT_CUSTOM_STORAGE', defaultValue: 'Custom Knowledge Storage' },
  { key: 'PLAN_ENT_CUSTOM_FOLDERS', defaultValue: 'Custom Knowledge Folder limits' },
  { key: 'PLAN_ENT_UNLIMITED_AGENTS', defaultValue: 'Unlimited Agents' },
  { key: 'PLAN_ENT_INTEGRATIONS', defaultValue: '1,000+ ready-to-use Integrations' },
  { key: 'PLAN_ENT_CUSTOM_INTEGRATIONS', defaultValue: 'Custom integrations' },
  { key: 'PLAN_ENT_SSO', defaultValue: 'Company Login / SSO' },
  { key: 'PLAN_ENT_BRANDING', defaultValue: 'Company Branding' },
  { key: 'PLAN_ENT_ONBOARDING', defaultValue: 'Tailored Onboarding' },
  { key: 'PLAN_ENT_PRIORITY_SUPPORT', defaultValue: 'Priority Support' },
  { key: 'PLAN_ENT_CUSTOM_DEPLOYMENT', defaultValue: 'Custom deployment and configuration options' },
];

export const planBestFor = (code?: string) => {
  if (!code) return undefined;
  return (
    PLAN_BEST_FOR[code.toLowerCase()] ??
    (isEnterprisePlan(code) ? PLAN_BEST_FOR.enterprise : undefined)
  );
};

export const planSeatMaxQuantity = (
  items: Pick<ApiPlanQuantityItem, 'itemKey' | 'unitLabel' | 'maxQuantity'>[]
): number | undefined => {
  const seat = items.find((item) => {
    const key = `${item.itemKey} ${item.unitLabel}`.toLowerCase();
    return key.includes('seat') || key.includes('user');
  });
  return seat?.maxQuantity ?? undefined;
};

export const planIncludedMeterLine = (
  meter: MeterCopy,
  t: Translate,
  seatMax?: number
): string => {
  const count = meter.includedQuantity.toLocaleString();
  const key = meterHaystack(meter);
  const period = String(t('MONTHLY', 'Monthly')).toLowerCase();

  if (key.includes('credit')) {
    if (seatMax) {
      return t('PLAN_INCLUDED_USERS_WITH_CREDITS', {
        users: seatMax.toLocaleString(),
        credits: count,
        defaultValue: 'Up to {{users}} users with {{credits}} KLAX Credits',
      });
    }
    return t('PLAN_INCLUDED_KLAX_CREDITS', {
      count,
      period,
      defaultValue: '{{count}} KLAX Credits / {{period}}',
    });
  }
  if (key.includes('folder')) {
    return t('PLAN_INCLUDED_KNOWLEDGE_FOLDERS', {
      count,
      defaultValue: 'Up to {{count}} Knowledge Folders',
    });
  }
  if (key.includes('storage') || key.includes('byte')) {
    const size = key.includes('byte') ? bytesToGbLabel(meter.includedQuantity) : count;
    return t('PLAN_INCLUDED_KNOWLEDGE_STORAGE', {
      size,
      defaultValue: '{{size}} GB Knowledge Storage',
    });
  }

  return t('PLAN_INCLUDED_METER', {
    count,
    name: meter.displayName,
    defaultValue: '{{count}} {{name}}',
  });
};

/** What a plan gives each person, and who it is best for. */
export const PlanIncluded = ({ plan }: { plan: ApiPlan }) => {
  const { t } = useTranslation();
  const isEnterprise = isEnterprisePlan(plan.code);
  const includedMeters = isEnterprise ? [] : includedPlanMeters(plan.meters);
  const seatMax = isEnterprise ? undefined : planSeatMaxQuantity(plan.quantityItems);
  const bestFor = planBestFor(plan.code);

  return (
    <>
      {(includedMeters.length > 0 || isEnterprise) && (
        <>
          <p className="mb-4 font-sans text-sm font-medium text-[#171b23] dark:text-foreground">
            {isEnterprise
              ? t('INCLUDED', 'Included')
              : t('INCLUDED_PER_USER', 'Included per user')}
          </p>
          <ul className="flex flex-col gap-3">
            {isEnterprise
              ? ENTERPRISE_INCLUDED.map((item) => (
                  <li
                    key={item.key}
                    className="flex items-start gap-2.5 font-sans text-sm text-[#171b23] dark:text-foreground"
                  >
                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#171b23] dark:text-foreground" />
                    <span>{t(item.key, item.defaultValue)}</span>
                  </li>
                ))
              : includedMeters.map((meter) => (
                  <li
                    key={meter.meterKey}
                    className="flex items-start gap-2.5 font-sans text-sm text-[#171b23] dark:text-foreground"
                  >
                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#171b23] dark:text-foreground" />
                    <span>{planIncludedMeterLine(meter, t, seatMax)}</span>
                  </li>
                ))}
          </ul>
        </>
      )}
      {bestFor && (
        <p className="mt-auto pt-10 font-sans text-sm leading-5 text-[#565f6b] dark:text-muted-foreground">
          {t('BEST_FOR', 'Best for:')} {t(bestFor.key, bestFor.defaultValue)}
        </p>
      )}
    </>
  );
};

/** Plans are per seat: the button moves the signed-in person's own seat to this plan. */
export interface SeatChoice {
  isCurrent: boolean;
  busy: boolean;
  /** This plan's seats when its first payment is still open, to finish or drop that checkout. */
  pendingCheckout?: PlanSeats;
  /** The cycle of this plan's existing subscription. Seats added to it stay on it, so no toggle is shown. */
  boughtInterval?: string;
  /** The billing cycle showing on the card, used for a first buy of this plan. */
  onSwitch: (interval: string) => void;
}

interface SubscriptionPlanProps {
  plan: ApiPlan;
  seat?: SeatChoice;
}

export const SubscriptionPlan = ({ plan, seat }: SubscriptionPlanProps) => {
  const { t } = useTranslation();
  const isDark = useTheme().resolvedTheme === 'dark';
  const navigate = useNavigate();
  const { mutate: subscribePlan, isPending, data, isSuccess } = useSubscribePlan();
  const { mutate: changePlan, isPending: isChangingPlan } = useChangeSubscriptionPlan();
  const { mutateAsync: submitContactForm } = useContactSales();
  const { configurationSettings } = useConfig();
  const { currentLanguage } = useLanguageContext();
  const { toast } = useToast();
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const user = useCommonStore((s) => s.user);
  const organizationId = selectedOrgId || user?.organizationId || '';
  const isMutating = useIsMutating({ mutationKey: ['plan-subscription'] });
  const subscriptionDetails = useSubscriptionStore((s) => s.subscriptionDetails);
  const [isContactOpen, setIsContactOpen] = useState(false);

  const planIntervals = useMemo(() => uniquePlanIntervals(plan.prices), [plan.prices]);
  // The backend stores one description per plan, so PLAN_DESCRIPTION_<CODE> translates it; the backend text is the fallback.
  const description = plan.description
    ? t(`PLAN_DESCRIPTION_${plan.code.toUpperCase().replace(/-/g, '_')}`, plan.description)
    : '';
  const userChoseInterval = useRef(false);
  const [selectedInterval, setSelectedInterval] = useState(() =>
    resolveDefaultInterval(uniquePlanIntervals(plan.prices), plan, subscriptionDetails)
  );

  useEffect(() => {
    if (planIntervals.length > 0 && !planIntervals.includes(selectedInterval)) {
      setSelectedInterval(resolveDefaultInterval(planIntervals, plan, subscriptionDetails));
      return;
    }
    if (userChoseInterval.current) return;
    const preferred = resolveDefaultInterval(planIntervals, plan, subscriptionDetails);
    if (preferred !== selectedInterval) {
      setSelectedInterval(preferred);
    }
  }, [plan, planIntervals, selectedInterval, subscriptionDetails]);

  const handleIntervalChange = (interval: string) => {
    userChoseInterval.current = true;
    setSelectedInterval(interval);
  };

  const boughtInterval = seat?.boughtInterval;
  const shownInterval = boughtInterval && planIntervals.includes(boughtInterval) ? boughtInterval : selectedInterval;
  const selectedPrice =
    plan.prices.find((p) => p.interval.toLowerCase() === shownInterval) ??
    plan.prices[0] ??
    null;
  const isUsageBased = plan.meters.length > 0;
  const hasPrice = Boolean(selectedPrice);
  const isFreePlan =
    plan.code?.toLowerCase() === 'free' ||
    Number(selectedPrice?.unitAmountMinor) === 0 ||
    (!hasPrice && !isUsageBased);
  const isEnterprise = isEnterprisePlan(plan.code);
  const periodLabel = billingCycleLabel(
    selectedPrice?.interval ?? shownInterval,
    (key, fallback) => t(key, fallback)
  ).toLowerCase();
  const displayAmountMinor = selectedPrice
    ? discountedAmountMinor(
        selectedPrice.unitAmountMinor,
        selectedPrice.automaticDiscountBasisPoints
      )
    : 0;
  const hasPriceDiscount =
    Boolean(selectedPrice) && displayAmountMinor !== selectedPrice.unitAmountMinor;

  const [isSwitchPlanModalOpen, setIsSwitchPlanModalOpen] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<{
    subscriptionId: string;
    planCode: string;
    priceId: string;
    quantities: { itemKey: string; quantity: number }[];
    interval?: string;
  } | null>(null);

  const isCurrentPlan = seat
    ? seat.isCurrent
    : (isEnterprise &&
        Boolean(subscriptionDetails?.subscriptionId) &&
        isEnterprisePlan(subscriptionDetails?.planCode)) ||
      isCurrentPlanOffer(plan, selectedPrice, subscriptionDetails);
  const isPendingCheckout = !seat && isIncompleteCheckout(subscriptionDetails?.status);
  const isPendingCheckoutPlan = isPendingCheckout && isCurrentPlan;

  const openCheckout = (checkoutUrl?: string | null) => {
    if (!checkoutUrl) return false;
    window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    return true;
  };

  useEffect(() => {
    if (isSuccess && data) {
      const checkoutUrl = (data as any)?.data?.checkoutUrl;
      openCheckout(checkoutUrl);
    }
  }, [isSuccess, data]);

  const handleContactSubmit = async (data: {
    name: string;
    company: string;
    email: string;
    phone?: string;
    message: string;
  }) => {
    return submitContactForm({
      language: currentLanguage,
      message: data.message,
      organizationName: data.company,
      contactName: data.name,
      phone: data.phone,
      emailTo: configurationSettings.paths.salesContactMailTo,
      email: data.email,
    });
  };

  const handlePlanSelect = () => {
    if (isEnterprise) {
      if (isCurrentPlan && !isPendingCheckoutPlan) return;
      setIsContactOpen(true);
      return;
    }

    if (seat) {
      seat.onSwitch(shownInterval);
      return;
    }

    if (isPendingCheckoutPlan) {
      if (!openCheckout(subscriptionDetails?.checkoutUrl)) {
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t(
            'PAYMENT_PENDING_WARNING',
            'You have a payment pending. Please complete the payment or unsubscribe to choose a different plan.'
          ),
        });
      }
      return;
    }

    const action = resolvePlanSelectAction({
      planCode: plan.code,
      selectedPrice,
      quantityItems: plan.quantityItems,
      subscriptionDetails,
    });

    console.log('Plan select action:', action);

    if (action.type === 'blocked-incomplete') {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t(
          'PLAN_CHANGE_CHECKOUT_PENDING',
          'Finish or cancel your pending checkout before changing plans.'
        ),
      });
      return;
    }

    if (action.type === 'change-plan') {
      setPendingPlanChange({
        subscriptionId: action.subscriptionId,
        planCode: action.planCode,
        priceId: action.priceId,
        quantities: action.quantities,
        interval: action.interval,
      });
      setIsSwitchPlanModalOpen(true);
      return;
    }

    if (action.type === 'subscribe') {
      // Navigate to checkout page with plan and selected price
      navigate('/checkout', {
        state: {
          plan: plan,
          selectedPrice: selectedPrice
        }
      });
    }
  };

  const handleConfirmSwitchPlan = () => {
    if (!pendingPlanChange) return;
    changePlan(pendingPlanChange, {
      onSuccess: () => {
        setIsSwitchPlanModalOpen(false);
        setPendingPlanChange(null);
      },
    });
  };

  const handleSwitchPlanModalChange = (open: boolean) => {
    setIsSwitchPlanModalOpen(open);
    if (!open) setPendingPlanChange(null);
  };

  const canSubscribe = isEnterprise || isFreePlan || hasPrice;
  const currentPlanName = subscriptionDetails?.planName || subscriptionDetails?.planCode || '';
  const targetPlanName = plan.displayName || plan.code;
  const isSamePlanName =
    currentPlanName.trim().toLowerCase() === targetPlanName.trim().toLowerCase();
  const currentBillingCycle = isSamePlanName
    ? billingCycleSuffix(
      subscriptionDetails?.interval ||
      plan.prices.find((p) => p.priceId === subscriptionDetails?.priceId)?.interval
    )
    : undefined;
  const targetBillingCycle = isSamePlanName
    ? billingCycleSuffix(selectedPrice?.interval ?? pendingPlanChange?.interval)
    : undefined;
  const cycleClassName = 'font-normal text-[#565f6b] dark:text-muted-foreground';

  return (
    <Card
      className={cn(
        'row-span-5 grid h-full min-h-[680px] min-w-0 grid-rows-subgrid overflow-hidden rounded-3xl border bg-white p-0 font-sans shadow-none',
        'border-[#e8e4d8] dark:border-border dark:bg-card',
        isPendingCheckoutPlan && 'border-amber-400/60 ring-1 ring-amber-500/20 dark:border-amber-500/40',
        isCurrentPlan && !isPendingCheckoutPlan && 'border-emerald-400/60 ring-1 ring-emerald-500/20 dark:border-emerald-500/40'
      )}
    >
      <div className="col-span-full row-span-4 grid grid-rows-subgrid bg-gradient-to-b from-[#fff7b8] to-white px-6 pt-7 dark:from-amber-900/35 dark:to-card">
        <h2 className="font-sans text-[1.75rem] font-bold leading-tight text-[#171b23] dark:text-foreground">
          {plan.displayName}
        </h2>

        <div className="grid min-h-[6.25rem] w-full grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:grid-rows-[auto]">
          {isEnterprise ? (
            <p className="font-sans text-[1.75rem] font-bold leading-none text-[#171b23] dark:text-foreground">
              {t('CUSTOM', 'Custom')}
            </p>
          ) : hasPrice && selectedPrice ? (
            <>
              <div className="min-w-0">
                {hasPriceDiscount && (
                  <span className="mb-1 block font-sans text-sm font-medium leading-none text-[#8b8a84] line-through dark:text-muted-foreground">
                    {formatCurrency(selectedPrice.unitAmountMinor, selectedPrice.currencyCode)}
                  </span>
                )}
                <p className="font-sans text-[1.75rem] font-bold leading-none text-[#171b23] dark:text-foreground">
                  {formatCurrency(displayAmountMinor, selectedPrice.currencyCode)}
                </p>
                <p className="mt-1.5 font-sans text-sm text-[#565f6b] dark:text-muted-foreground">
                  {t('PLAN_PRICE_PER_USER', {
                    period: periodLabel,
                    defaultValue: '/ user / {{period}}',
                  })}
                  {hasPriceDiscount && selectedPrice.automaticDiscountBasisPoints
                    ? ` · ${t('PLAN_YEARLY_DISCOUNT', {
                        percent: selectedPrice.automaticDiscountBasisPoints / 100,
                        defaultValue: '{{percent}}% off',
                      })}`
                    : null}
                </p>
              </div>
              {!boughtInterval && planIntervals.length > 1 && (
                <BillingCycleToggle
                  intervals={planIntervals}
                  selectedInterval={selectedInterval}
                  disabled={isPending || isChangingPlan || isMutating > 0}
                  onChange={handleIntervalChange}
                />
              )}
            </>
          ) : isFreePlan ? (
            <p className="font-sans text-[1.75rem] font-bold leading-none text-[#171b23] dark:text-foreground">
              {t('FREE', 'Free')}
            </p>
          ) : (
            <span className="rounded-md border border-[#d9a15a] bg-[#fbf1e6] px-3 py-2 font-sans text-xs text-[#8a5a1e] dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
              {t('NO_PRICE_CONFIGURED', 'no price configured — cannot be subscribed to yet')}
            </span>
          )}
        </div>

        <p
          title={description || undefined}
          className="line-clamp-2 font-sans text-sm leading-5 text-[#565f6b] dark:text-muted-foreground"
        >
          {description || '\u00a0'}
        </p>

        <div className="flex min-h-14 items-end pb-6">
          {seat?.pendingCheckout && <PendingCheckoutAction entry={seat.pendingCheckout} />}
          {canSubscribe && !seat?.pendingCheckout && (
            <Button
              className={cn(
                'flex w-full cursor-pointer items-center justify-center gap-2 rounded-full py-6 text-base font-semibold transition-colors',
                isCurrentPlan && !isPendingCheckoutPlan
                  ? 'cursor-default border border-emerald-500/30 !bg-emerald-500/10 font-medium !text-emerald-600 opacity-100 hover:!bg-emerald-500/10 hover:!text-emerald-600 dark:border-emerald-500/40 dark:!bg-emerald-500/20 dark:!text-emerald-400 dark:hover:!bg-emerald-500/20 dark:hover:!text-emerald-400'
                  : cn('shadow-sm disabled:cursor-not-allowed disabled:opacity-50', primaryButtonClass(isDark))
              )}
              onClick={handlePlanSelect}
              disabled={
                isPending ||
                isChangingPlan ||
                !!seat?.busy ||
                isMutating > 0 ||
                (isCurrentPlan && !isPendingCheckoutPlan) ||
                (!isEnterprise && isPendingCheckout && !isPendingCheckoutPlan)
              }
            >
              {isPendingCheckoutPlan && <CreditCard className="h-4 w-4" />}
              {isCurrentPlan && !isPendingCheckoutPlan && (
                <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              )}
              {isPendingCheckoutPlan
                ? t('CONTINUE_PAYMENT', 'Continue Payment')
                : isCurrentPlan
                  ? t('CURRENT_PLAN', 'Current plan')
                  : isEnterprise
                    ? t('CONTACT_SALES', 'Contact Sales')
                    : seat
                      ? t('GET_PLAN_NAME', { plan: targetPlanName, defaultValue: 'Get {{plan}}' })
                      : isFreePlan
                        ? t('USE_FREE_PLAN', 'Use Free Plan')
                        : t('GET_STARTED', 'Get Started')}
              {(isPending || isChangingPlan) && <Loader2 className="h-4 w-4 animate-spin" />}
            </Button>
          )}
        </div>
      </div>

      <div className="col-span-full flex flex-col px-6 pb-6 pt-6">
        <PlanIncluded plan={plan} />
        <ContactModal
          isOpen={isContactOpen}
          onClose={() => setIsContactOpen(false)}
          onSubmit={handleContactSubmit}
          companyRequired
        />
        <ConfirmationModal
          open={isSwitchPlanModalOpen}
          onOpenChange={handleSwitchPlanModalChange}
          title={t('SWITCH_PLAN', 'Switch plan')}
          description={
            <>
              {t('SWITCH_PLAN_WARNING_PREFIX', 'You are currently subscribed to')}{' '}
              <strong className="font-bold text-foreground">{currentPlanName}</strong>
              {currentBillingCycle ? <span className={cycleClassName}>/{currentBillingCycle}</span> : null}
              {t('SWITCH_PLAN_WARNING_SUFFIX', '. Do you still want to switch?')}
            </>
          }
          confirmText={
            <>
              {t('SWITCH_TO_PLAN_PREFIX', 'Switch to')} {targetPlanName}
              {targetBillingCycle ? (
                <span className="font-normal text-primary-foreground/70">/{targetBillingCycle}</span>
              ) : null}
            </>
          }
          cancelText={t('CANCEL', 'Cancel')}
          preventAutoClose
          isLoading={isChangingPlan}
          onConfirm={handleConfirmSwitchPlan}
        />
      </div>
    </Card>
  );
};
